import { ExternalLink, Inbox } from "lucide-react";
import { Skeleton } from "../../../../components/ui/skeleton";
import { SideBadge } from "./SideBadge";
import { EmptyState } from "./EmptyState";
import { cn } from "../../../../lib/utils";
import {
  formatDateTime,
  formatDecimal,
  truncateAddress,
} from "../../../../lib/format";

export function FillsTable({ fills, isLoading }) {
  if (!isLoading && fills && fills.length === 0) {
    return (
      <EmptyState
        icon={<Inbox className="h-4 w-4" />}
        title="No fills in window"
        description="Nothing matched the current filters. Try a wider range or remove the coin filter."
      />
    );
  }

  return (
    <>
      {/* Mobile: card list */}
      <div className="lg:hidden">
        <MobileList fills={fills} isLoading={isLoading} />
      </div>

      {/* Desktop: table */}
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr className="text-[10px]  font-sans uppercase tracking-[1px] text-muted-foreground">
              <Th align="left">Time</Th>
              <Th align="left">Coin</Th>
              <Th align="left">Side</Th>
              <Th align="left">Dir</Th>
              <Th align="right">Price</Th>
              <Th align="right">Size</Th>
              <Th align="right">Fee</Th>
              <Th align="right">Builder fee</Th>
              <Th align="left">Hash</Th>
            </tr>
          </thead>
          <tbody>
            {isLoading && !fills
              ? Array.from({ length: 8 }).map((_, i) => <RowSkeleton key={i} />)
              : fills?.map((f) => (
                  <tr key={f.tid}>
                    <Td>
                      <span className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                        {formatDateTime(f.fillTime)}
                      </span>
                    </Td>
                    <Td>
                      <span className="font-mono font-medium">{f.coin}</span>
                    </Td>
                    <Td>
                      <SideBadge side={f.side} />
                    </Td>
                    <Td>
                      <span className="whitespace-nowrap tracking-[0px] font-sans text-xs text-muted-foreground">
                        {f.dir}
                      </span>
                    </Td>
                    <Td align="right">
                      <span className="whitespace-nowrap font-mono">
                        {formatDecimal(f.px, 4)}
                      </span>
                    </Td>
                    <Td align="right">
                      <span className="whitespace-nowrap font-mono">
                        {formatDecimal(f.sz, 6)}
                      </span>
                    </Td>
                    <Td align="right">
                      <span className="whitespace-nowrap font-mono text-muted-foreground">
                        {formatDecimal(f.fee, 4)}
                      </span>
                    </Td>
                    <Td align="right">
                      <span className="whitespace-nowrap font-mono text-muted-foreground">
                        {f.builderFee ? formatDecimal(f.builderFee, 4) : "—"}
                      </span>
                    </Td>
                    <Td>
                      <a
                        href={`https://app.hyperliquid.xyz/explorer/tx/${f.hash}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 whitespace-nowrap font-mono text-xs text-muted-foreground hover:text-ginox-green"
                      >
                        {truncateAddress(f.hash, 6, 4)}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </Td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function MobileList({ fills, isLoading }) {
  if (isLoading && !fills) {
    return (
      <ul className="divide-y divide-border/60">
        {Array.from({ length: 6 }).map((_, i) => (
          <li key={i} className="px-5 py-4">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="mt-3 h-5 w-40" />
            <Skeleton className="mt-2 h-3 w-28" />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className="divide-y divide-border/60">
      {fills?.map((f) => (
        <li key={f.tid} className="px-5 py-4">
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span className="font-mono">{formatDateTime(f.fillTime)}</span>
            <a
              href={`https://app.hyperliquid.xyz/explorer/tx/${f.hash}`}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 font-mono hover:text-ginox-green"
            >
              {truncateAddress(f.hash, 5, 4)}
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          <div className="mt-2.5 flex items-center gap-2">
            <span className="font-mono text-sm font-medium text-foreground">
              {f.coin}
            </span>
            <SideBadge side={f.side} />
            <span className="min-w-0 truncate text-xs text-muted-foreground">
              {f.dir}
            </span>
          </div>

          <div className="mt-2 flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5 font-mono text-sm">
            <span className="text-foreground">{formatDecimal(f.px, 4)}</span>
            <span className="text-muted-foreground/50">×</span>
            <span className="text-foreground">{formatDecimal(f.sz, 6)}</span>
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>
              Fee{" "}
              <span className="font-mono text-foreground/80">
                {formatDecimal(f.fee, 4)}
              </span>
            </span>
            <span className="text-muted-foreground/30">·</span>
            <span>
              Builder{" "}
              <span className="font-mono text-foreground/80">
                {f.builderFee ? formatDecimal(f.builderFee, 4) : "—"}
              </span>
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className={cn(
        "border-b border-border/70 px-5 py-3 font-medium",
        align === "right" ? "text-right" : "text-left",
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left" }) {
  return (
    <td
      className={cn(
        "border-b border-border/40 px-5 py-3.5",
        align === "right" ? "text-right" : "text-left",
      )}
    >
      {children}
    </td>
  );
}

function RowSkeleton() {
  return (
    <tr>
      {Array.from({ length: 9 }).map((_, i) => (
        <td key={i} className="border-b border-border/40 px-5 py-3.5">
          <Skeleton className="h-4 w-full max-w-[120px]" />
        </td>
      ))}
    </tr>
  );
}
